import React, { FC } from 'react';
import cn from 'classnames';
import Slider from '@wix/da-ds/pkg/Slider';
import { useEditorContext } from '../../Editor/Context';
import { useForceUpdate } from '../../../hooks/useForceUpdate';
import Bold from '../buttons/Bold';
import Italic from '../buttons/Italic';
import Strike from '../buttons/Strike';
import Underline from '../buttons/Underline';
import BlockFormatting from '../buttons/BlockFormatting';
import BulletList from '../buttons/BulletList';
import OrderedList from '../buttons/OrderedList';
import HRule from '../buttons/HRule';
import Blockquote from '../buttons/Blockquote';
import ClearFormatting from '../buttons/ClearFormatting';
import Undo from '../buttons/Undo';
import Redo from '../buttons/Redo';
import Alignment from '../buttons/Alignment';
import Separator from '../buttons/Separator';
import Link from '../buttons/Link';
import FontFamily from '../buttons/FontFamily';
import FontColor from '../buttons/FontColor';
import FontHighlight from '../buttons/FontHighlight';
import LinkAnchor from '../buttons/LinkAnchor';
import Reaction from '../buttons/Reaction';
import Deviation from '../buttons/Deviation';
import Gallery from '../buttons/Gallery';
import Gif from '../buttons/Gif';
import SliderNext from '../buttons/SliderNext';
import SliderPrev from '../buttons/SliderPrev';
import AddMedia from '../buttons/AddMedia';
import List from '../buttons/List';
import TextDecoration from '../buttons/TextDecoration';
import Indent from '../buttons/Indent';
import Outdent from '../buttons/Outdent';
import Video from '../buttons/Video';
import { ThemeSurface } from '@wix/da-react-context/pkg/constants';
import { ThemeOverride } from '@wix/da-react-context/pkg/ThemeContext';
import { isTouch as getIsTouch } from '../../utils/browser';

import s from './EverythingMenu.scss';

export interface Props {
  wrapping?: boolean;
  noAtomics?: boolean;
  omitHRule?: boolean;
  className?: string;
}

const EverythingMenu: FC<Props> = ({
  wrapping = true,
  noAtomics = false,
  omitHRule = false,
  className,
}) => {
  const editor = useEditorContext();
  const isTouch = getIsTouch();
  const forceUpdate = useForceUpdate();

  // Listen to editor as its not react so we need to react here
  editor?.on('transaction', () => {
    forceUpdate();
  });

  if (!editor) {
    return null;
  }

  let buttons = (
    <>
      <Undo editor={editor} />
      <Redo editor={editor} />

      <Separator editor={editor} />

      {!noAtomics && <Reaction editor={editor} />}
      {!noAtomics && <Deviation editor={editor} />}
      {!noAtomics && <Gallery editor={editor} />}
      {!noAtomics && <Gif editor={editor} />}
      {!noAtomics && <Video editor={editor} />}

      {!noAtomics && <Separator editor={editor} />}

      <BlockFormatting editor={editor} />

      <Separator editor={editor} hideWhen={'heading'} />

      <Bold editor={editor} />
      <Italic editor={editor} />
      <Underline editor={editor} />
      <Strike editor={editor} />
      <FontFamily editor={editor} />
      <FontColor editor={editor} />
      <FontHighlight editor={editor} />

      <Separator editor={editor} />

      <Link editor={editor} />
      <LinkAnchor editor={editor} />

      <Separator editor={editor} />

      <Alignment editor={editor} />
      <Outdent editor={editor} />
      <Indent editor={editor} />
      <Blockquote editor={editor} />
      <OrderedList editor={editor} />
      <BulletList editor={editor} />
      {!omitHRule && <HRule editor={editor} />}
      <Separator editor={editor} />
      <ClearFormatting editor={editor} />
    </>
  );

  if (isTouch) {
    buttons = (
      <>
        <Undo editor={editor} />
        <Redo editor={editor} />

        <Separator editor={editor} />

        {!noAtomics && <Reaction editor={editor} />}
        {!noAtomics && <AddMedia editor={editor} />}
        {!noAtomics && <Gif editor={editor} />}

        {!noAtomics && <Separator editor={editor} />}

        <Link editor={editor} />

        {!noAtomics && <Separator editor={editor} />}

        <Bold editor={editor} />
        <Italic editor={editor} />
        <TextDecoration editor={editor} />
        <FontFamily editor={editor} />
        <FontColor editor={editor} />
        <FontHighlight editor={editor} />

        <BlockFormatting editor={editor} />
        <Alignment editor={editor} />
        <Indent editor={editor} />
        <Outdent editor={editor} />
        <Blockquote editor={editor} />
        <List editor={editor} />
        {!omitHRule && <HRule editor={editor} />}
      </>
    );
  }

  return (
    <ThemeOverride
      themeSurface={ThemeSurface.TERTIARY}
      className={cn(s['root'], wrapping && s['wrapping'], className)}
    >
      {!wrapping && (
        <Slider
          permArrows
          forceMobileArrows
          renderNextButton={props => <SliderNext editor={editor} {...props} />}
          renderPrevButton={props => <SliderPrev editor={editor} {...props} />}
          contentClassName="menu-scrollable"
        >
          {buttons}
        </Slider>
      )}
      {wrapping && buttons}
    </ThemeOverride>
  );
};
EverythingMenu.displayName = 'EverythingMenu';

export default EverythingMenu;
